/***********************
 * INDEX TAB4 CONTENT (VISITS)
 ************************/

import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import {
  Row,
  Col,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Card,
  CardBody,
  Tooltip,
} from "reactstrap"

// Datatable related plugins
import "../datatables.scss"
import { Table as AntdTable, Input as AntdInput } from "antd"

//Excel export
import XLSX from "xlsx"

//Autotable PDF
import jsPDF from "jspdf"
import "jspdf-autotable"

//Locale
import { withTranslation } from "react-i18next"
import {
  timeFormatter,
  dateFormatter,
  showToast,
  defaultPageSizeOptions,
  stringSorter,
  numberSorter,
  getMedian,
} from "utils/general"
import { tableDefaultHeight } from "constants/layout"

const { Search } = AntdInput

const VisitsReporting = props => {
  const [itemsLength, setItemsLength] = useState([0])
  const [searchText, setSearchText] = useState("")
  //Table data state
  const [filteredData, setFilteredData] = useState([])
  // Card states
  const [durationA, setDurationA] = useState(0)
  const [durationM, setDurationM] = useState(0)
  //Dropdown button state
  const [menu, setMenu] = useState(false)
  // Tooltip state for download button
  const [tTdownloadstatstable, setTTdownloadstatstable] = useState(false)

  //Excel and pdf name
  const title = props.t("Visits")

  // Handle Search and Set new state data when props.data or searchText changes
  useEffect(() => {
    if (props.activeTab == 5) {
      const delayDebounce = setTimeout(() => {
        const lower = searchText.toLowerCase()

        const filtered = props.data.list.filter(item => {
          const durationText = timeFormatter(item.duration) || ""
          const dateText = dateFormatter(item.date) || ""
          const visitor = item.visitor?.toLowerCase() || ""
          const client = item.client_name?.toLowerCase() || ""
          const group = item.group?.toLowerCase() || ""

          return (
            durationText.includes(lower) ||
            dateText.includes(lower) ||
            visitor.includes(lower) ||
            client.includes(lower) ||
            group.includes(lower)
          )
        })
        console.log("filtered:", filtered)
        setFilteredData(filtered)

        // Calculate and set avg and median durations
        const durations = filtered.map(item => item.duration)
        setDurationA(
          Math.trunc(
            durations.reduce((sum, val) => sum + val, 0) / durations.length
          )
        )
        setDurationM(getMedian(durations))
        setItemsLength(durations.length)
      }, 300)

      return () => clearTimeout(delayDebounce)
    }
  }, [searchText, props.data])

  // Table structure
  const columns1 = [
    {
      dataIndex: "date",
      title: props.t("Time"),
      defaultSortOrder: "descend",
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["date"], b["date"], sortOrder)
      },
      render: dateFormatter,
      width: "165px",
    },
    {
      dataIndex: "client_name",
      title: props.t("Customers name"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "client_name", sortOrder)
      },
      width: "200px",
    },
    {
      dataIndex: "group",
      title: props.t("Group"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "group", sortOrder)
      },
      width: "195px",
    },
    {
      dataIndex: "visitor",
      title: props.t("Visitor"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "visitor", sortOrder)
      },
      width: "195px",
    },
    {
      dataIndex: "duration",
      title: props.t("Duration"),
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["duration"], b["duration"], sortOrder)
      },
      render: timeFormatter,
      width: "100px",
    },
  ]

  // Function for formatting seconds -> hours, minutes, seconds
  const formatTime = value => {
    let hours = Math.floor(value / 3600)
    let minutes = Math.floor((value - hours * 3600) / 60)
    let seconds = value - hours * 3600 - minutes * 60

    if (hours != 0) {
      hours = hours + "h "
    }
    if (minutes != 0) {
      minutes = minutes + "min "
    }
    if (hours == 0) {
      hours = ""
    }
    if (minutes == 0) {
      minutes = ""
    }
    return hours + minutes + seconds + "s"
  }

  //Times
  const timeStats = [
    {
      id: 0,
      value: durationA,
      text: props.t("Average"),
      series: formatTime(durationA),
    },
    {
      id: 1,
      value: durationM,
      text: props.t("Median"),
      series: formatTime(durationM),
    },
  ]

  // Export PDF table
  const exportPDF = () => {
    let message = props.t("PDF file exported succesfully!")
    const unit = "pt"
    const size = "A4" // Use A1, A2, A3 or A4
    const orientation = "landscape" // portrait or landscape
    const theme = "grid" //striped,  plain

    const marginLeft = 40
    const doc = new jsPDF(orientation, unit, size, theme)

    doc.setFontSize(14)

    const data = filteredData.map(elt => [
      dateFormatter(elt.date),
      elt.client_name,
      elt.group,
      elt.visitor,
      timeFormatter(elt.duration),
    ])

    let content = {
      startY: 50,
      head: [
        [
          props.t("Time"),
          props.t("Customers name"),
          props.t("Group"),
          props.t("Visitor"),
          props.t("Duration"),
        ],
      ],
      body: data,
    }

    doc.text(title, marginLeft, 40)
    doc.autoTable(content)
    doc.save(title + ".pdf")

    //Show toast
    showToast(message, "success", 5000, "toast-bottom-left")
  }

  // Creating Excel workbook+worksheet+download
  const downloadExcel = () => {
    let message = props.t("Excel file exported succesfully!")
    const wb = XLSX.utils.book_new()

    const Heading = [
      [
        props.t("Time"),
        props.t("Customers name"),
        props.t("Group"),
        props.t("Visitor"),
        props.t("Duration"),
      ],
    ]
    const data = filteredData.map(elt => [
      dateFormatter(elt.date),
      elt.client_name,
      elt.group,
      elt.visitor,
      timeFormatter(elt.duration),
    ])

    // creating sheet and adding data from 2nd row of column A.
    // Leaving first row to add Heading
    const ws = XLSX.utils.json_to_sheet(data, {
      origin: "A2",
      skipHeader: true,
    })
    XLSX.utils.sheet_add_aoa(ws, Heading, { origin: "A1" })
    XLSX.utils.book_append_sheet(wb, ws, title)
    XLSX.write(wb, { bookType: "xlsx", type: "buffer" })
    XLSX.writeFile(wb, title + " (" + new Date() + ")" + ".xlsx")

    //Show toast
    showToast(message, "success", 5000, "toast-bottom-left")
  }

  return (
    <>
      {/* Setting filter for view */}
      <Row className=" mt-0 mb-2">
        <div className="d-flex flex-wrap">
          <span>
            <h5 className="mt-0">{props.t("Visits")}</h5>
          </span>
        </div>
      </Row>
      <Row>
        <Col lg={props.quickGuide ? 6 : 3} xxl={3}>
          <Card>
            <CardBody>
              <Row>
                <Col className="mt-2 text-center" xs="12">
                  <div className="mb-1 ">
                    <div className="mb-4">
                      <i className="mdi mdi-home-map-marker text-primary display-6" />
                    </div>
                    <div className="mt-2 mb-2">
                      <h3>{itemsLength}</h3>
                      {itemsLength > 1 ? (
                        <p className="">{props.t("made visits")}</p>
                      ) : (
                        <p className="">{props.t("made visit")}</p>
                      )}
                    </div>
                  </div>
                </Col>
                <Col xs="12">
                  {/* Duration of all calls */}
                  <div className="mt-5 mb-3">
                    <span className="font-size-14 h5">
                      {props.t("Visit duration")}
                    </span>
                  </div>

                  {timeStats.map(time => (
                    <div key={time.id + "_div"}>
                      <div className="py-2">
                        <div>
                          {time.text}
                          <span className="font-size-14 h5 float-end">
                            {time.series}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col lg={props.quickGuide ? 12 : 9} xxl={9}>
          <Card>
            <CardBody className="pb-0">
              <Row>
                <Col className="col-12">
                  <React.Fragment>
                    <Row className="align-items-center justify-content-end">
                      <Col className="col-auto">
                        <Search
                          placeholder={props.t("Search")}
                          allowClear
                          value={searchText}
                          onChange={e => setSearchText(e.target.value)}
                          style={{ width: "180px" }}
                        />
                      </Col>
                      <Col className="col-auto">
                        <span className="mt-0">
                          <Tooltip
                            placement="bottom"
                            isOpen={tTdownloadstatstable}
                            target="downloadstatstable"
                            toggle={() => {
                              setTTdownloadstatstable(!tTdownloadstatstable)
                            }}
                          >
                            {props.t("Download statistics")}
                          </Tooltip>
                          <Dropdown
                            id="printButtonServicesStats"
                            isOpen={menu}
                            toggle={() => setMenu(!menu)}
                            className="download-btn text-center"
                            type="button"
                          >
                            <DropdownToggle
                              id="downloadstatstable"
                              className="text-muted"
                              tag="i"
                            >
                              <i className="mdi mdi-download " />
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => downloadExcel()}>
                                {props.t("Download .xlsx")}
                              </DropdownItem>
                              <DropdownItem onClick={() => exportPDF()}>
                                {props.t("Download .pdf")}
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </span>
                      </Col>
                    </Row>

                    <Row className="mt-3">
                      <Col xl="12">
                        <AntdTable
                          className="light-header-table table align-middle table-nowrap mb-0"
                          bordered={false}
                          size="small"
                          rowKey={"id"}
                          columns={columns1}
                          dataSource={filteredData}
                          pagination={{
                            showSizeChanger: true,
                            defaultPageSize: 50,
                            pageSizeOptions: defaultPageSizeOptions,
                          }}
                          tableLayout="auto"
                          scroll={{
                            x: "max-content",
                            y: tableDefaultHeight,
                          }}
                        />
                      </Col>
                    </Row>
                  </React.Fragment>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

VisitsReporting.propTypes = {
  t: PropTypes.any,
  data: PropTypes.any,
  activeTab: PropTypes.any,
  quickGuide: PropTypes.any,
}

export default withTranslation()(VisitsReporting)
